/**
 * A flat, thumbnail-first list of evidence files with inline preview and
 * download - the same row treatment the claim Evidence card uses, without the
 * claim-specific uploader grouping / soft-delete machinery. Surfaces whose
 * evidence is a simple attached-file list (e.g. inspections) render this
 * directly; each item is already normalised by the caller.
 *
 * Follows the platform empty-state contract: loading -> error -> empty ->
 * content, with the loading gate defeating the cached-empty-then-refetch flash
 * (a caller with a genuine "first load" passes `isLoading`/`isFetching`; a
 * caller whose data is already resolved by a parent query passes neither and
 * the list goes straight to empty -> content).
 */
import { useState } from 'react'
import { Loader2 } from 'lucide-react'

import {
  EvidenceFileRow,
  type EvidenceFileRowProps,
} from '@/shared/components/evidence/evidence-file-row'
import {
  FileViewerDialog,
  type ViewableFile,
} from '@/shared/components/file-viewer-dialog'

export interface EvidenceFileListItem
  extends Omit<EvidenceFileRowProps, 'onPreview' | 'onDelete' | 'isDeleting' | 'onDownload'> {
  /** Stable React key for the row. */
  key: string
}

export function EvidenceFileList({
  items,
  isLoading = false,
  isFetching = false,
  isError = false,
  emptyMessage = 'No evidence attached',
  errorMessage = "Couldn't load evidence. Try refreshing.",
  loadingMessage = 'Loading evidence…',
  className,
}: {
  items: EvidenceFileListItem[]
  isLoading?: boolean
  isFetching?: boolean
  isError?: boolean
  emptyMessage?: string
  errorMessage?: string
  loadingMessage?: string
  className?: string
}) {
  const [previewing, setPreviewing] = useState<ViewableFile | null>(null)
  const showLoading = isLoading || (isFetching && items.length === 0)

  return (
    <>
      {showLoading ? (
        <div className="flex items-center justify-center py-6 text-[11px] text-[var(--ink-3)]">
          <Loader2 className="mr-2 h-3 w-3 animate-spin" />
          {loadingMessage}
        </div>
      ) : isError ? (
        <div className="px-3 py-4 text-center text-[11px] text-rose-600">{errorMessage}</div>
      ) : items.length === 0 ? (
        <div className="px-3 py-4 text-center text-[11px] text-[var(--ink-3)]">{emptyMessage}</div>
      ) : (
        <ul
          className={
            className ??
            'divide-y divide-[var(--rule)] overflow-hidden rounded-lg border border-[var(--rule)] bg-[var(--bg-card)]'
          }
        >
          {items.map(({ key, ...row }) => (
            <EvidenceFileRow
              key={key}
              {...row}
              onPreview={
                row.fileUuid || row.fileUrl
                  ? () =>
                      setPreviewing({
                        file_uuid: row.fileUuid ?? null,
                        file_url: row.fileUrl ?? null,
                        file_name: row.fileName,
                        mime_type: row.mimeType ?? undefined,
                      })
                  : undefined
              }
            />
          ))}
        </ul>
      )}

      <FileViewerDialog
        file={previewing}
        onOpenChange={(open) => {
          if (!open) setPreviewing(null)
        }}
      />
    </>
  )
}
